// Component based on: https://github.com/codefreeze8/aurelia-select2
import { EventAggregator } from 'aurelia-event-aggregator';
import { bindable, customElement, inject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import $ from 'jquery';
import 'select2';
import { IdTextPair } from 'select2';

const formatHtml = (item: { text: string; class: string; element: HTMLElement }) => {
  // to allow pass-thru of HTML which comes as glyph icons.
  let className = '';
  if (item.text.indexOf('<') > -1) {
    // must pass back as jquery object to by-pass string-sanitizer
    const $state = $("<span class='" + item.class + "'>" + item.text + '</span>');
    return $state;
  }
  if (item.element) {
    className = item.element.className;
  } else {
    className = '';
  }
  return $("<span class='" + className + "'>" + item.text + '</span>');
};

@customElement('select2')
@inject(Element, I18N, EventAggregator)
export class Select2 {
  public lang: string;

  @bindable protected name = null; // name/id of custom select
  //  @bindable selected = []; // default selected value
  @bindable protected options = null; // array of options with id/name properties
  @bindable protected optionsQuery: Promise<Array<{ Id?: number | string; Inactive?: boolean; IsDeleted?: boolean }>>;
  @bindable protected placeholder = '';
  @bindable protected allowClear = true;
  @bindable protected includeEmptyOption = true;
  @bindable protected multiple = false;
  @bindable protected selected: string;
  @bindable protected valueField: string;
  @bindable protected labelField: string;
  @bindable protected sortfield: string;
  @bindable protected sortDirection: 'ASC' | 'DESC' = 'ASC';
  @bindable protected disabled = false;
  @bindable protected customClass = '';
  @bindable protected allowAddNumericOptions = false;
  @bindable protected dontResetOnLanguageChange = false;

  private element: HTMLElement;
  private SELECT: JQuery<HTMLSelectElement>;
  private selectedDecoupled: string | number;

  private label: string;
  private isPerformingQuery = false;

  constructor(element: HTMLElement, private i18N: I18N, private eventAggregator: EventAggregator) {
    this.element = element;
    this.selectedDecoupled = '';
  }

  protected attached() {
    this.SELECT = $(this.element).find('select');
    this.lang = this.i18N.getLocale();
    this.selectedDecoupled = this.selected;

    // if (this.disabled) {
    //   select2options.disabled = true;
    // }

    const select = this.SELECT.select2({
      templateSelection: formatHtml,
      templateResult: formatHtml,
      disabled: !!this.disabled || false,
      tags: this.allowAddNumericOptions ? true : false,
      createTag: (params) => {
        const term = params.term?.trim();
        // Only allow integers
        if (!/^\d+$/.test(term)) {
          return null; // Reject non-numeric tags
        }
        const numericValue = Number(term);
        return {
          id: numericValue,
          text: term,
          newTag: true,
        } as unknown as IdTextPair;
      },
    }) as unknown as JQuery<HTMLSelectElement>;

    this.getOptions();

    select.on('change', (event: { originalEvent: string; target: HTMLInputElement; delegatedTarget: { selected: string } }) => {
      // don't propagate endlessly
      // see: http://stackoverflow.com/a/34121891/4354884
      if (event.originalEvent || this.isPerformingQuery) {
        return;
      }

      const value = event.delegatedTarget?.selected || event.target.value;

      // dispatch to raw select within the custom element
      // bubble it up to allow change handler on custom element
      const notice = new CustomEvent('change', {
        detail: {
          value: value,
          values: this.SELECT.select2('data'),
        },
        bubbles: true,
      });
      $(this.SELECT)[0].dispatchEvent(notice);
    });

    this.eventAggregator.subscribe('lang-changed', () => {
      if (!this.dontResetOnLanguageChange) {
        this.lang = this.i18N.getLocale();
      }
    });

    setTimeout(() => {
      this.element.querySelector('.select2-selection')?.addEventListener('focus', () => {
        this.SELECT.select2('open');
      });
    }, 100);
  }

  // Enable dynamic changing of disabled attribute
  protected disabledChanged(newV: unknown) {
    if (!this.SELECT) {
      return;
    }

    this.SELECT.select2({ disabled: Boolean(newV), tags: true });
  }

  protected selectedChanged(newV: string | number) {
    this.getOptions();
    if (this.SELECT && newV != this.selectedDecoupled) {
      this.SELECT.val(newV);
      this.SELECT.trigger('change');
      this.selectedDecoupled = newV;
    }
  }

  private getOptions() {
    if (this.optionsQuery) {
      this.isPerformingQuery = true;
      void this.optionsQuery.then((res) => {
        this.isPerformingQuery = false;
        this.options = res.filter((x) => x.Id == this.selected || (!x.Inactive && !x.IsDeleted));
      });
    }
  }

  // Aurelia lifecycle
  protected detached() {
    if (this.SELECT && this.SELECT.hasClass('select2-hidden-accessible')) {
      this.SELECT.select2('destroy');
      this.SELECT.off('change');
    }
  }
}
